import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { BACKEND_URL } from '../config';
import { useApp } from '../contexts/AppContext';
import {
  FeedbackAvisIcon,
  FeedbackBugIcon,
  FeedbackIdeaIcon,
  FeedbackOtherIcon,
} from './Icons';
import { decodeJwtPayload, isJwtExpired } from '../lib/jwt';
import type { Session } from '../types/app';
import type { BetaFeedbackCategory } from '../types/system';
import {
  buildBetaFeedbackContext,
  getCurrentPageLabel,
  getPageContextExtraDetails,
  type BetaFeedbackOccurrence,
} from '../utils/betaFeedbackContext';
import '../styles/system-status.css';

interface BetaFeedbackModalProps {
  session: Session;
  open: boolean;
  onClose: () => void;
}

const CATEGORY_ORDER: BetaFeedbackCategory[] = ['bug', 'idea', 'satisfaction', 'other'];

const CATEGORY_META: Record<BetaFeedbackCategory, { label: string; icon: ReactNode }> = {
  bug: { label: 'Bug', icon: <FeedbackBugIcon size={13} /> },
  idea: { label: 'Idée', icon: <FeedbackIdeaIcon size={13} /> },
  satisfaction: { label: 'Avis', icon: <FeedbackAvisIcon size={13} /> },
  other: { label: 'Autre', icon: <FeedbackOtherIcon size={13} /> },
};

const OCCURRENCE_LABELS: Record<BetaFeedbackOccurrence, string> = {
  first_time: 'Première fois',
  recurring: 'Récurrent',
  unknown: 'Je ne sais pas',
};

const RATING_LABELS: Record<number, string> = {
  0: 'Très mécontent',
  1: 'Mécontent',
  2: 'Plutôt insatisfait',
  3: 'Correct',
  4: 'Satisfait',
  5: 'Très satisfait',
};

const MAX_SCREENSHOT_BYTES = 2_000_000;
const MAX_SCREENSHOTS = 5;

interface ScreenshotItem {
  name: string;
  dataUrl: string;
}

function formatBetaFeedbackError(code: string): string {
  switch (code) {
    case 'invalid_token':
      return 'Session expirée ou invalide — déconnectez-vous puis reconnectez-vous pour envoyer un feedback.';
    case 'missing_token':
      return 'Session expirée — reconnectez-vous.';
    case 'api_unreachable':
      return 'Service d\'authentification indisponible. Réessayez dans un instant.';
    case 'invalid_rating':
      return 'Choisissez une note entre 0 et 5 étoiles.';
    default:
      return code;
  }
}

function StarGlyph({ filled }: { filled: boolean }) {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" aria-hidden>
      <path
        d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}

export default function BetaFeedbackModal({ session, open, onClose }: BetaFeedbackModalProps) {
  const {
    activeTab,
    gameInfo,
    trainingInfo,
    tablesCategories,
    chatUnreadCount,
    chatConnected,
  } = useApp();
  const [category, setCategory] = useState<BetaFeedbackCategory>('bug');
  const [message, setMessage] = useState('');
  const [concernsCurrentPage, setConcernsCurrentPage] = useState(false);
  const [occurrence, setOccurrence] = useState<BetaFeedbackOccurrence>('unknown');
  const [rating, setRating] = useState<number | null>(null);
  const [screenshots, setScreenshots] = useState<ScreenshotItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isSatisfaction = category === 'satisfaction';
  const pageLabel = getCurrentPageLabel(activeTab);

  const pageExtraDetails = useMemo(() => getPageContextExtraDetails({
    activeTab,
    gameInfo,
    trainingInfo,
    tablesCategories,
    chatUnreadCount,
    chatConnected,
    occurrence,
    rating,
  }), [
    activeTab,
    chatConnected,
    chatUnreadCount,
    gameInfo,
    occurrence,
    rating,
    tablesCategories,
    trainingInfo,
  ]);

  const introText = useMemo(() => {
    if (isSatisfaction) {
      return concernsCurrentPage
        ? `Notez votre expérience sur la page « ${pageLabel} ».`
        : 'Notez votre expérience globale avec Neo.';
    }
    return 'Signalez un bug, partagez une idée ou décrivez un problème.';
  }, [concernsCurrentPage, isSatisfaction, pageLabel]);

  useEffect(() => {
    if (!open) return;
    setCategory('bug');
    setMessage('');
    setConcernsCurrentPage(false);
    setOccurrence('unknown');
    setRating(null);
    setScreenshots([]);
    setError(null);
    setSuccess(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [open]);

  const handleCategoryChange = useCallback((next: BetaFeedbackCategory) => {
    setCategory(next);
    setError(null);
    if (next === 'satisfaction') {
      setScreenshots([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } else {
      setRating(null);
    }
  }, []);

  const handleScreenshotPick = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;

    const remaining = MAX_SCREENSHOTS - screenshots.length;
    if (remaining <= 0) {
      setError(`Maximum ${MAX_SCREENSHOTS} images.`);
      return;
    }

    const toAdd = Array.from(files).slice(0, remaining);
    const errors: string[] = [];

    for (const file of toAdd) {
      if (!file.type.startsWith('image/')) {
        errors.push(`${file.name} : format non supporté`);
        continue;
      }
      if (file.size > MAX_SCREENSHOT_BYTES) {
        errors.push(`${file.name} : trop volumineuse (max 2 Mo)`);
        continue;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const result = typeof reader.result === 'string' ? reader.result : null;
        if (!result) {
          setError(`Lecture de ${file.name} impossible.`);
          return;
        }
        setScreenshots(prev => {
          if (prev.length >= MAX_SCREENSHOTS) return prev;
          return [...prev, { name: file.name, dataUrl: result }];
        });
        setError(null);
      };
      reader.onerror = () => setError(`Lecture de ${file.name} impossible.`);
      reader.readAsDataURL(file);
    }

    if (errors.length > 0) {
      setError(errors.join(' · '));
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [screenshots.length]);

  const removeScreenshot = useCallback((index: number) => {
    setScreenshots(prev => prev.filter((_, i) => i !== index));
  }, []);

  const submit = useCallback(async () => {
    const trimmed = message.trim();

    if (isSatisfaction) {
      if (rating === null) {
        setError('Choisissez une note entre 0 et 5.');
        return;
      }
    } else if (trimmed.length < 5) {
      setError('Décrivez votre retour en au moins 5 caractères.');
      return;
    }

    if (!session.token) {
      setError('Session expirée — reconnectez-vous.');
      return;
    }

    const jwtPayload = decodeJwtPayload(session.token);
    if (isJwtExpired(jwtPayload) && !session.token.startsWith('dev_')) {
      setError('Votre session a expiré — déconnectez-vous puis reconnectez-vous.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const context = buildBetaFeedbackContext({
        activeTab,
        gameInfo,
        trainingInfo,
        tablesCategories,
        chatUnreadCount,
        chatConnected,
        concernsCurrentPage,
        occurrence: isSatisfaction ? undefined : occurrence,
        rating: isSatisfaction ? rating : undefined,
      });
      if (!isSatisfaction && screenshots.length > 0) {
        context.screenshots = screenshots.map(item => item.dataUrl);
      }

      const res = await fetch(`${BACKEND_URL}/api/beta-feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.token}`,
          'X-Pseudo-Display': session.pseudoDisplay,
        },
        body: JSON.stringify({
          category,
          message: trimmed,
          context,
        }),
      });
      const data = await res.json() as { error?: string; message?: string; success?: boolean };
      if (!res.ok) {
        throw new Error(formatBetaFeedbackError(data.error || data.message || 'Envoi impossible'));
      }
      setSuccess(true);
      setMessage('');
      setRating(null);
      setScreenshots([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setBusy(false);
    }
  }, [
    activeTab,
    category,
    chatConnected,
    chatUnreadCount,
    concernsCurrentPage,
    gameInfo,
    isSatisfaction,
    message,
    occurrence,
    rating,
    screenshots,
    session.pseudoDisplay,
    session.token,
    tablesCategories,
    trainingInfo,
  ]);

  if (!open) return null;

  return (
    <div className="beta-feedback-overlay" role="presentation" onClick={onClose}>
      <div
        className="beta-feedback-modal"
        role="dialog"
        aria-labelledby="beta-feedback-title"
        aria-modal="true"
        onClick={e => e.stopPropagation()}
      >
        <div className="beta-feedback-header">
          <h2 id="beta-feedback-title">Feedback beta-test</h2>
          <button type="button" className="beta-feedback-close" onClick={onClose} aria-label="Fermer">
            ×
          </button>
        </div>

        {success ? (
          <div className="beta-feedback-success">
            <p>Merci ! Votre retour a bien été enregistré.</p>
            <button type="button" className="system-status-btn system-status-btn--primary" onClick={onClose}>
              Fermer
            </button>
          </div>
        ) : (
          <>
            <p className="beta-feedback-intro">{introText}</p>

            <div className="beta-feedback-field">
              <span className="beta-feedback-field-label">Type</span>
              <div className="beta-feedback-pills beta-feedback-pills--types" role="group" aria-label="Type de retour">
                {CATEGORY_ORDER.map(key => (
                  <button
                    key={key}
                    type="button"
                    className={`beta-feedback-pill beta-feedback-pill--with-icon${category === key ? ' beta-feedback-pill--active' : ''}`}
                    onClick={() => handleCategoryChange(key)}
                    disabled={busy}
                    aria-pressed={category === key}
                  >
                    <span className="beta-feedback-pill-icon" aria-hidden>{CATEGORY_META[key].icon}</span>
                    <span>{CATEGORY_META[key].label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="beta-feedback-page-toggle">
              <button
                type="button"
                role="switch"
                className={`beta-feedback-switch${concernsCurrentPage ? ' beta-feedback-switch--on' : ''}`}
                aria-checked={concernsCurrentPage}
                aria-label={`Concerne spécifiquement la page ${pageLabel}`}
                onClick={() => setConcernsCurrentPage(prev => !prev)}
                disabled={busy}
              >
                <span className="beta-feedback-switch-thumb" aria-hidden />
              </button>
              <span className="beta-feedback-page-toggle-label">
                Concerne spécifiquement la page «&nbsp;{pageLabel}&nbsp;»
              </span>
            </div>
            {concernsCurrentPage && pageExtraDetails && (
              <p className="beta-feedback-page-details">{pageExtraDetails}</p>
            )}

            {isSatisfaction ? (
              <div className="beta-feedback-field">
                <span className="beta-feedback-field-label">
                  {concernsCurrentPage ? `Note pour « ${pageLabel} »` : 'Note pour Neo'}
                </span>
                <div className="beta-feedback-stars" role="group" aria-label="Note de satisfaction">
                  <button
                    type="button"
                    className={`beta-feedback-star-zero${rating === 0 ? ' beta-feedback-star-zero--active' : ''}`}
                    onClick={() => setRating(0)}
                    disabled={busy}
                    aria-pressed={rating === 0}
                    aria-label="Très mécontent"
                  >
                    <span className="beta-feedback-star-anger" aria-hidden>😠</span>
                  </button>
                  {[1, 2, 3, 4, 5].map(value => (
                    <button
                      key={value}
                      type="button"
                      className={`beta-feedback-star-btn${rating != null && value <= rating ? ' beta-feedback-star-btn--active' : ''}`}
                      onClick={() => setRating(value)}
                      disabled={busy}
                      aria-pressed={rating === value}
                      aria-label={`${value} étoile${value > 1 ? 's' : ''}`}
                    >
                      <StarGlyph filled={rating != null && value <= rating} />
                    </button>
                  ))}
                </div>
                {rating != null && (
                  <p className="beta-feedback-rating-hint">{RATING_LABELS[rating]}</p>
                )}
              </div>
            ) : (
              <div className="beta-feedback-field">
                <span className="beta-feedback-field-label">Fréquence</span>
                <div className="beta-feedback-pills beta-feedback-pills--wrap" role="group" aria-label="Fréquence du problème">
                  {(Object.keys(OCCURRENCE_LABELS) as BetaFeedbackOccurrence[]).map(key => (
                    <button
                      key={key}
                      type="button"
                      className={`beta-feedback-pill${occurrence === key ? ' beta-feedback-pill--active' : ''}`}
                      onClick={() => setOccurrence(key)}
                      disabled={busy}
                      aria-pressed={occurrence === key}
                    >
                      {OCCURRENCE_LABELS[key]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <label className="beta-feedback-field" htmlFor="beta-feedback-message">
              <span className="beta-feedback-field-label">
                {isSatisfaction ? 'Commentaire' : 'Description'}
                {isSatisfaction && (
                  <span className="beta-feedback-field-hint">optionnel</span>
                )}
              </span>
              <textarea
                id="beta-feedback-message"
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder={
                  isSatisfaction
                    ? 'Précisez ce qui vous a plu ou déplu…'
                    : 'Que s\'est-il passé ? Que suggérez-vous ?'
                }
                rows={isSatisfaction ? 3 : 5}
                disabled={busy}
                maxLength={5000}
              />
            </label>

            {!isSatisfaction && (
              <div className="beta-feedback-screenshot">
                <span className="beta-feedback-field-label">
                  Captures d&apos;écran
                  <span className="beta-feedback-field-hint">optionnel · max {MAX_SCREENSHOTS} × 2 Mo</span>
                </span>
                <input
                  id="beta-feedback-screenshot"
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="beta-feedback-file-input"
                  disabled={busy || screenshots.length >= MAX_SCREENSHOTS}
                  onChange={e => handleScreenshotPick(e.target.files)}
                />
                <button
                  type="button"
                  className="beta-feedback-upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={busy || screenshots.length >= MAX_SCREENSHOTS}
                >
                  Ajouter des images
                </button>
                {screenshots.length > 0 && (
                  <ul className="beta-feedback-screenshot-list">
                    {screenshots.map((item, index) => (
                      <li key={`${item.name}-${index}`} className="beta-feedback-screenshot-item">
                        <img src={item.dataUrl} alt="" className="beta-feedback-screenshot-thumb" />
                        <span className="beta-feedback-screenshot-name">{item.name}</span>
                        <button
                          type="button"
                          className="beta-feedback-screenshot-remove"
                          onClick={() => removeScreenshot(index)}
                          disabled={busy}
                          aria-label={`Retirer ${item.name}`}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {error && <p className="beta-feedback-error">{error}</p>}

            <div className="beta-feedback-actions">
              <button type="button" className="system-status-btn" onClick={onClose} disabled={busy}>
                Annuler
              </button>
              <button
                type="button"
                className="system-status-btn system-status-btn--primary"
                onClick={() => void submit()}
                disabled={busy}
              >
                {busy ? 'Envoi…' : 'Envoyer'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
